import React, { useEffect, useState } from 'react';
import { useTable, usePagination } from 'react-table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Paginator } from 'primereact/paginator';
import { useNavigate } from 'react-router-dom';
import { useStoreSelector, useAuthStatus, useStoreBrnDSSelector } from '../../store/selectors/userSelector';
import { useTopicWordApi } from '../../store/api/hooks/topicwordApiHook';
import toast from 'react-hot-toast';
import moment from 'moment';
import Icon from '../../components/icons';
import ReviewDetailsModal from './reviewDetailsModal';
import Spinner from '../../components/iconSvgs/spinner';
import { TopicDataTableProps } from '../../_config/interfaces/interfaces';

const TopicDataTable: React.FC<TopicDataTableProps> = ({ filterValue }) => {
    const [isOnSubmitLoading, setOnSubmitLoading] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [first, setFirst] = useState(1);
    const [perPage, setPerpage] = useState(10);
    const [page, setPage] = useState(1);
    const [isPagination, setIsPagination] = useState<boolean>(false)
    const [topicData, setTopicData] = useState<any[]>([]);
    const [topicReviewsData, setTopicReviewsData] = useState<any>({});
    const [totalcount, setTotalCount] = useState(0);
    const [hovered, setHovered] = useState<{ index: number | null, type: string | null }>({ index: null, type: null });

    const topicAPI = useTopicWordApi();
    const navigate = useNavigate();
    const branchDS = useStoreBrnDSSelector();

    useEffect(() => {
        if (filterValue !== null) {
            applyFilter(filterValue); // This is called automatically when filterValue changes
        } else {
            getTopics({});
        }
    }, [filterValue]);

    const applyFilter = (filterValue: any) => {
        getTopics(filterValue);
    }

    const getTopics = (param: any) => {
        setOnSubmitLoading(true);
        let params: any;
        if (param) {
            params = {
                branch_id: branchDS?.branch_id, page: Math.floor(first / perPage) + 1,
                page_size: perPage, ...param
            };
        } else {
            params = {
                'branch_id': branchDS?.branch_id,
                'page': Math.floor(first / perPage) + 1, // Page is 1-based, adjust accordingly
                'page_size': perPage
            };
        }
        try {
            setTimeout(() => {
                topicAPI.getTopics(params, (msg: any, resp: any) => {
                    if (resp && resp.data && resp.data.length > 0) {
                        resp.data.map((item: any) => {
                            const positive = item.sentiment_distribution?.positive?.count ?? 0;
                            const neutral = item.sentiment_distribution?.neutral?.count ?? 0;
                            const negative = item.sentiment_distribution?.negative?.count ?? 0;
                            const mixed = item.sentiment_distribution?.mixed?.count ?? 0;

                            const totalSentiment = positive + neutral + negative + mixed;

                            let positiveWidth = 0, neutralWidth = 0, negativeWidth = 0, mixedWidth = 0;
                            // let positiveCount = positive, neutralCount = neutral, negativeCount = negative, mixedCount = mixed;

                            if (totalSentiment > 0) {
                                positiveWidth = (positive / totalSentiment) * 100;
                                neutralWidth = (neutral / totalSentiment) * 100;
                                negativeWidth = (negative / totalSentiment) * 100;
                                mixedWidth = (mixed / totalSentiment) * 100;

                                item.sentiment_distribution.positiveCount = positive;
                                item.sentiment_distribution.neutralCount = neutral;
                                item.sentiment_distribution.negativeCount = negative;
                                item.sentiment_distribution.mixedCount = mixed;

                                item.sentiment_distribution.positiveWidth = positiveWidth;
                                item.sentiment_distribution.neutralWidth = neutralWidth;
                                item.sentiment_distribution.negativeWidth = negativeWidth;
                                item.sentiment_distribution.mixedWidth = mixedWidth;
                            }

                        })

                        setTopicData(resp.data);
                        setTotalCount(resp.pagination?.total_records || 0);
                        setIsPagination(true);
                    } else {
                        setTopicData([]);
                        setTotalCount(0);
                        setIsPagination(false);
                    }
                }, (err: any) => {
                    toast.error(err);
                });
                setOnSubmitLoading(false);
            }, 3000);
        } catch (e) {
            console.log('error =>', e);
        }

    };

    const handlePageChange = (e: any) => {
        const newPage = e.page;
        const newFirst = e.first;
        const newRows = e.rows;

        if (newFirst !== first || newRows !== perPage) {
            setPage(newPage);
            setPerpage(perPage)
            setFirst(newFirst);

            let params: any;
            if (filterValue) {
                params = {
                    branch_id: branchDS?.branch_id, page: newPage + 1,
                    page_size: perPage, ...filterValue
                };
            } else {
                params = {
                    'branch_id': branchDS?.branch_id,
                    'page': newPage + 1, // Page is 1-based, adjust accordingly
                    'page_size': perPage
                };
            }
            getTopics(params);
        }
    };

    const onClickInfoModal = (index: any) => {
        setTopicReviewsData(topicData[index].sentiment_distribution);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div>
            {isOnSubmitLoading && (
                <div className="absolute inset-0 bg-gray-700 bg-opacity-50 flex justify-center items-center z-50">
                    <Spinner className="self-center z-50" />
                </div>
            )}
            <table className="min-w-full table-auto">
                <thead>
                    <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600">
                        <th className="px-4 py-2" style={{ width: '300px' }}>Topic</th>
                        <th className="px-4 py-2" style={{ width: '400px' }}>Sentiment</th>
                        <th className="px-4 py-2" style={{ width: '200px' }}>Mentions</th>
                        <th className="px-4 py-2" style={{ width: '200px' }}>Overall Reviews</th>
                        <th className="px-4 py-2" style={{ width: '200px' }}>Trends</th>
                    </tr>
                </thead>
                <tbody className='scrollY-auto'>
                    {topicData.map((topic, index: any) => {
                        return (
                            <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                <td className="px-4 py-4 capitalize items-center justify-center">
                                    <div className='flex items-center'>
                                        <Icon name="info" className="text-gray-500 mr-2 cursor-pointer" onClick={() => onClickInfoModal(index)} />
                                        <span>{topic.topic}</span>
                                    </div>
                                    <ReviewDetailsModal isOpen={isModalOpen} onClose={closeModal} reviewDetails={topicReviewsData} />
                                </td>
                                <td className="px-4 py-2">
                                    <div className="relative flex w-full bg-gray-300 rounded-full h-2">
                                        {/* <>{console.log('positiveCount =>', positiveCount)}</> */}
                                        <div
                                            className="h-2 bg-green-500 rounded-l-full"
                                            style={{ width: `${topic?.sentiment_distribution?.positiveWidth}%`, cursor: 'pointer' }}
                                            onMouseEnter={() => setHovered({ index, type: 'positive' })} onMouseLeave={() => setHovered({ index, type: null })}

                                        >
                                            {hovered.index === index && hovered.type === 'positive' && (
                                                <div
                                                    className="absolute left-1/2 transform -translate-x-1/2 text-gray-700 text-base bg-blue-200 p-2 rounded shadow w-32 flex flex-col items-center justify-center space-y-2"
                                                    style={{ bottom: '15px' }} // Add top property to create the gap
                                                >
                                                    <p>Positive: {topic?.sentiment_distribution?.positiveCount}</p>
                                                    <p>Negative: {topic?.sentiment_distribution?.negativeCount}</p>
                                                    <p>Mixed: {topic?.sentiment_distribution?.mixedCount}</p>
                                                    <p>Neutral: {topic?.sentiment_distribution?.neutralCount}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className="h-2 bg-red-500 rounded-r-full"
                                            style={{ width: `${topic?.sentiment_distribution?.negativeWidth}%` }} onMouseEnter={() => setHovered({ index, type: 'negative' })}
                                            onMouseLeave={() => setHovered({ index, type: null })}
                                        >
                                            {hovered.index === index && hovered.type === 'negative' && (
                                                <div
                                                    className="absolute left-1/2 transform -translate-x-1/2 text-gray-700 text-base bg-blue-200 p-2 rounded shadow w-32 flex flex-col items-center justify-center space-y-2"
                                                    style={{ bottom: '15px' }} // Add top property to create the gap
                                                >
                                                    <p>Positive: {topic?.sentiment_distribution?.positiveCount}</p>
                                                    <p>Negative: {topic?.sentiment_distribution?.negativeCount}</p>
                                                    <p>Mixed: {topic?.sentiment_distribution?.mixedCount}</p>
                                                    <p>Neutral: {topic?.sentiment_distribution?.neutralCount}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className="h-2 bg-gray-500 rounded-r-full"
                                            style={{ width: `${topic?.sentiment_distribution?.mixedWidth}%`, cursor: 'pointer' }} onMouseEnter={() => setHovered({ index, type: 'mixed' })}
                                            onMouseLeave={() => setHovered({ index, type: null })}
                                        >
                                            {hovered.index === index && hovered.type === 'mixed' && (
                                                <div
                                                    className="absolute left-1/2 transform -translate-x-1/2 text-gray-700 text-base bg-blue-200 p-2 rounded shadow w-32 flex flex-col items-center justify-center space-y-2"
                                                    style={{ bottom: '15px' }} // Add top property to create the gap
                                                >
                                                    <p>Positive: {topic?.sentiment_distribution?.positiveCount}</p>
                                                    <p>Negative: {topic?.sentiment_distribution?.negativeCount}</p>
                                                    <p>Mixed: {topic?.sentiment_distribution?.mixedCount}</p>
                                                    <p>Neutral: {topic?.sentiment_distribution?.neutralCount}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className="h-2 bg-yellow-500"
                                            style={{ width: `${topic?.sentiment_distribution?.neutralWidth}%`, cursor: 'pointer' }} onMouseEnter={() => setHovered({ index, type: 'neutral' })}
                                            onMouseLeave={() => setHovered({ index, type: null })}

                                        >
                                            {hovered.index === index && hovered.type === 'neutral' && (
                                                <div
                                                    className="absolute left-1/2 transform -translate-x-1/2 text-gray-700 text-base bg-blue-200 p-2 rounded shadow w-32 flex flex-col items-center justify-center space-y-2"
                                                    style={{ bottom: '15px' }} // Add top property to create the gap
                                                >
                                                    <p>Positive: {topic?.sentiment_distribution?.positiveCount}</p>
                                                    <p>Negative: {topic?.sentiment_distribution?.negativeCount}</p>
                                                    <p>Mixed: {topic?.sentiment_distribution?.mixedCount}</p>
                                                    <p>Neutral: {topic?.sentiment_distribution?.neutralCount}</p>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </td>

                                <td className="px-4 py-2">{topic.mentions}</td>
                                <td className="px-4 py-2">{topic.overall_reviews}%</td>
                                <td className="px-4 py-2">
                                    <div style={{ width: '100%', height: '120px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={topic.trend}
                                                syncId="anyId"
                                                margin={{
                                                    top: 10,
                                                    // right: 30,
                                                    left: -41,
                                                    bottom: 0,
                                                }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} />
                                                <XAxis dataKey="date" tickFormatter={(tick) => moment(tick).format('DD MMM')} tick={false} />
                                                <YAxis tickFormatter={(value) => value % 1 === 0 ? value : ''}
                                                    domain={[0, Math.max(...topic.trend.map((item: any) => item?.positive + item?.negative + item?.mixed + item?.neutral))]}
                                                    tickCount={5} tick={false} hide={true} />
                                                {/* <Tooltip /> */}
                                                {/* <Legend /> */}
                                                <Area
                                                    type="monotone"
                                                    dataKey="mentions"
                                                    stroke="#3B82F680"
                                                    fill="#3B82F680"
                                                // fillOpacity={0.3}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Pagination */}
            {isPagination && totalcount > 10 && (<div className="mt-4">
                <Paginator
                    first={first}
                    // rows={perPage}
                    rows={perPage}
                    totalRecords={totalcount}
                    // rowsPerPageOptions={[15, 30, 50]}
                    onPageChange={handlePageChange}
                    className="p-jc-center"
                />
            </div>)}
        </div>
    )

}

export default TopicDataTable;